import { useEffect, useMemo, useState } from "react";
import {
	Box,
	DialogContent,
	Grid,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { AccountBalanceWalletOutlined, PictureAsPdf } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Util } from "../../util/Util";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { openPdfFromBase64 } from "../voucher/voucherExport";

import { addMoney } from "../../util/quantity";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
}

type Line = { amount?: number; mode?: string };
type DepositRecord = {
	batch?: string;
	date?: string;
	user?: string;
	description?: string;
	income?: number;
	expense_total?: number;
	amount?: number;
	payments?: Line[];
	wallets?: Line[];
	changes?: Line[];
	expenses?: Line[];
	refunds?: Line[];
	wallet_debits?: Line[];
};

const sumAmount = (rows?: Line[]) =>
	(rows || []).reduce((sum, row) => addMoney(sum, Number(row.amount || 0)), 0);

const Report = ({ isOpen, toggle, bundle }: Props) => {
	const [record, setRecord] = useState<DepositRecord | null>(null);
	const { api } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const currency = appSettings?.BIZ_CURRENCY?.value || "";

	const fetchData = async () => {
		if (!bundle?.batch) return;
		setLoadingMessage("FETCH");
		const res = await api.get(`deposits/${bundle.batch}`);
		setLoadingMessage(null);
		if (res?.status === 200) {
			setRecord(res.data);
			return;
		}
		if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchData);
			return;
		}
		if (res?.status === 404) {
			toast.warn(t("common:toast.warn.noRecord"));
			toggle();
			return;
		}
		console.error("Failed to fetch deposit:", res);
	};

	useEffect(() => {
		if (isOpen && bundle?.batch) {
			setRecord(null);
			void fetchData();
		}
	}, [isOpen, bundle?.batch]);

	const handleExport = async () => {
		if (!bundle?.batch) return;
		setLoadingMessage(t("common:report.loadingReport"));
		const res = await api.get(`/reports/deposits/${bundle.batch}`);
		setLoadingMessage(null);
		if (res?.status === 200 && res.data?.data) {
			openPdfFromBase64(res.data.data);
			return;
		}
		if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleExport);
			return;
		}
		toast.error(t("common:deposits.report.failedToLoad"));
	};

	const sections = useMemo(() => {
		if (!record) return [];
		return [
			{ key: "payments", label: t("common:deposits.modal.paymentsTitle"), rows: record.payments, expense: false },
			{ key: "wallets", label: t("common:wallet.title"), rows: record.wallets, expense: false },
			{ key: "changes", label: t("common:deposits.modal.changesTitle"), rows: record.changes, expense: false },
			{ key: "expenses", label: t("common:expense.title"), rows: record.expenses, expense: true },
			{ key: "refunds", label: t("common:deposits.pendingDetail.refundCount"), rows: record.refunds, expense: true },
			{ key: "wallet_debits", label: t("common:deposits.pendingDetail.walletDebitCount"), rows: record.wallet_debits, expense: true },
		]
			.map((section) => ({
				...section,
				count: (section.rows || []).length,
				amount: sumAmount(section.rows),
			}))
			.filter((section) => section.count > 0);
	}, [record, t]);

	const modes = useMemo(() => {
		const totals = new Map<string, { count: number; amount: number }>();
		for (const section of sections) {
			for (const row of section.rows || []) {
				const mode = String(row.mode || "—");
				const current = totals.get(mode) || { count: 0, amount: 0 };
				const signed = section.expense ? -Number(row.amount || 0) : Number(row.amount || 0);
				totals.set(mode, { count: current.count + 1, amount: current.amount + signed });
			}
		}
		return [...totals.entries()]
			.map(([mode, data]) => ({ mode, ...data }))
			.sort((a, b) => a.mode.localeCompare(b.mode));
	}, [sections]);

	const formatMoney = (value: number) =>
		`${Util.formatNumber(value)}${currency ? ` ${currency}` : ""}`;

	const Kpi = ({ label, value }: { label: string; value: number }) => (
		<Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default", height: "100%" }}>
			<Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.6, textTransform: "uppercase", fontWeight: 700 }}>
				{label}
			</Typography>
			<Typography variant="h6" fontWeight={800}>
				{formatMoney(value)}
			</Typography>
		</Paper>
	);

	return (
		<MoleDialog open={isOpen} onClose={toggle} maxWidth="md" fullWidth themeColor={themeColor} paperOptions={{ maxHeight: "95vh" }}>
			<MoleDialogHeader
				title={t("common:deposits.report.title")}
				subtitle={bundle?.batch}
				icon={<AccountBalanceWalletOutlined />}
				onClose={toggle}
				themeColor={themeColor}
			/>
			<DialogContent sx={{ p: 3 }}>
				{record ? (
					<Stack spacing={2.5}>
						<Grid container spacing={1.5}>
							<Grid size={{ xs: 12, sm: 4 }}>
								<Typography variant="caption" color="text.secondary">{t("common:tables.depositDate")}</Typography>
								<Typography fontWeight={700}>{record.date ? Util.formatDate(record.date, "long") : "—"}</Typography>
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<Typography variant="caption" color="text.secondary">{t("common:deposits.report.by")}</Typography>
								<Typography fontWeight={700}>{record.user || "—"}</Typography>
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<Typography variant="caption" color="text.secondary">{t("common:tables.deposit")}</Typography>
								<Typography fontWeight={700}>{record.batch}</Typography>
							</Grid>
						</Grid>

						<Grid container spacing={1.5}>
							<Grid size={{ xs: 12, sm: 4 }}>
								<Kpi label={t("common:deposits.modal.income")} value={Number(record.income || 0)} />
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<Kpi label={t("common:deposits.modal.expense")} value={Number(record.expense_total || 0)} />
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<Kpi label={t("common:deposits.modal.net")} value={Number(record.amount || 0)} />
							</Grid>
						</Grid>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
							<Paper variant="outlined" sx={{ overflow: "hidden" }}>
								<Typography variant="subtitle2" sx={{ px: 1.5, py: 1, fontWeight: 700 }}>
									{t("common:deposits.pendingDetail.byType")}
								</Typography>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>{t("common:tables.type")}</TableCell>
											<TableCell align="right">{t("common:tables.count")}</TableCell>
											<TableCell align="right">{t("common:tables.amount")}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{sections.map((section) => (
											<TableRow key={section.key}>
												<TableCell>{section.label}</TableCell>
												<TableCell align="right">{section.count}</TableCell>
												<TableCell align="right">
													{section.expense
														? `(${Util.formatNumber(section.amount)})`
														: Util.formatNumber(section.amount)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Paper>

							<Paper variant="outlined" sx={{ overflow: "hidden" }}>
								<Typography variant="subtitle2" sx={{ px: 1.5, py: 1, fontWeight: 700 }}>
									{t("common:deposits.pendingDetail.byMode")}
								</Typography>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>{t("common:tables.mode")}</TableCell>
											<TableCell align="right">{t("common:tables.count")}</TableCell>
											<TableCell align="right">{t("common:tables.amount")}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{modes.map((row) => (
											<TableRow key={row.mode}>
												<TableCell>{row.mode}</TableCell>
												<TableCell align="right">{row.count}</TableCell>
												<TableCell align="right">
													{row.amount < 0
														? `(${Util.formatNumber(Math.abs(row.amount))})`
														: Util.formatNumber(row.amount)}
												</TableCell>
											</TableRow>
										))}
										<TableRow>
											<TableCell colSpan={2} sx={{ fontWeight: 800 }}>{t("common:deposits.modal.net")}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 800 }}>
												{Util.formatNumber(Number(record.amount || 0))}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</Paper>
						</Box>

						{record.description ? (
							<Typography variant="body2" color="text.secondary">
								{t("common:ui.labels.notes")} {record.description}
							</Typography>
						) : null}
					</Stack>
				) : (
					<Typography color="text.secondary">{t("common:status.loading")}</Typography>
				)}
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.close")}</MoleCancelButton>
				<MolePrimaryButton startIcon={<PictureAsPdf />} onClick={handleExport} disabled={!record}>
					{t("common:ui.buttons.export")}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default Report;
